import React, { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { OneWayInServiceDto } from 'src/api/v2';
import { useClusters } from 'src/features/cluster';
import { Tag } from '@innovationnorway/cl-react';
import { useTranslation } from 'react-i18next';
import { Counties } from 'src/features/one-way-in/constants/counties';
import useGeonorge from 'src/features/one-way-in/hooks/use-geonorge';
import { Link } from 'react-router-dom';

const columnHelper = createColumnHelper<OneWayInServiceDto>();

enum ServiceStatus {
  Active = 'active',
  Inactive = 'inactive',
  Draft = 'draft',
}

export const useServiceTableColumns = (status: string) => {
  const { t: tCommon } = useTranslation('common');
  return useMemo(() => {
    return [
      columnHelper.accessor('name', {
        id: 'name',
        minSize: 400,
        cell: (info) => <Link to={info.row.original.id}>{info.getValue()}</Link>,
      }),
      columnHelper.accessor('state', {
        id: 'status',
        minSize: 130,
        enableSorting: false,
        cell: () => {
          if (status === ServiceStatus.Active)
            return <Tag data-color={'success' as const}>{tCommon('active')}</Tag>;
          if (status === ServiceStatus.Draft)
            return <Tag data-color={'info' as const}>{tCommon('draft')}</Tag>;
          if (status === ServiceStatus.Inactive)
            return <Tag data-color={'neutral' as const}>{tCommon('inactive')}</Tag>;
        },
      }),
      columnHelper.accessor('contactName', {
        id: 'contactPerson',
        minSize: 250,
      }),
    ];
  }, [status, tCommon]);
};

export const useMyServicesInactiveTableColumns = (status: string) => {
  const { t: tCommon } = useTranslation('common');
  return useMemo(() => {
    return [
      columnHelper.accessor('name', {
        id: 'name',
        minSize: 400,
        cell: (info) => <Link to={info.row.original.id}>{info.getValue()}</Link>,
      }),
      columnHelper.accessor('state', {
        id: 'status',
        minSize: 130,
        enableSorting: false,
        cell: () => {
          if (status === ServiceStatus.Active)
            return <Tag data-color={'success' as const}>{tCommon('active')}</Tag>;
          if (status === ServiceStatus.Draft)
            return <Tag data-color={'info' as const}>{tCommon('draft')}</Tag>;
          if (status === ServiceStatus.Inactive)
            return <Tag data-color={'neutral' as const}>{tCommon('inactive')}</Tag>;
        },
      }),
    ];
  }, [status, tCommon]);
};

export const useAllServicesTableColumns = () => {
  const { clusters } = useClusters();
  const { getMunicipalityNumberByName } = useGeonorge();
  const { t: tOneWayIn } = useTranslation('oneWayIn');

  return useMemo(() => {
    const getCountiesByMunicipalitiesOrCluster = (
      serviceMunicipalities: string[],
      clusterId: string | undefined,
    ) => {
      if (clusterId === '00000000-0000-0000-0000-000000000000' && serviceMunicipalities.length === 0)
        return tOneWayIn('nationWide');
      // If no municipality is registered on the service, use the cluster county
      const clusterCounty = clusters.find((cluster) => cluster.id === clusterId)?.address?.county;
      if (!serviceMunicipalities || serviceMunicipalities.length === 0) return clusterCounty;

      // Find the county based on municipality number
      // If the municipality is not a number, convert to number and then find county
      const counties: string[] = [];
      for (const serviceMunicipality of serviceMunicipalities) {
        const municipalityNumber = isNaN(Number(serviceMunicipality))
          ? getMunicipalityNumberByName(serviceMunicipality)
          : serviceMunicipality;
        const county = Counties.find((county) =>
          county.municipalities.some((municipality) => municipality === municipalityNumber),
        );

        if (county && !counties.includes(county.countyName)) {
          counties.push(county.countyName);
        }
      }

      // If no counties are found, use the cluster county
      return counties.length > 0 ? counties.join(', ') : clusterCounty;
    };

    const getActorName = (clusterId: string | undefined, businessName: string | undefined) => {
      const clusterName = clusters.find((cluster) => cluster.id === clusterId)?.name;
      return clusterName ?? businessName;
    };

    return [
      columnHelper.accessor('name', {
        id: 'name',
        minSize: 400,
        cell: (info) => <Link to={info.row.original.id + '?activeTab=allServices'}>{info.getValue()}</Link>,
      }),
      columnHelper.accessor((row) => getActorName(row.clusterId, row.businessName), {
        id: 'actor',
        minSize: 400,
        cell: (info) => {
          return info.getValue();
        },
      }),
      columnHelper.accessor('contactName', {
        id: 'contactPerson',
        minSize: 250,
      }),
      columnHelper.accessor(
        (row) => getCountiesByMunicipalitiesOrCluster(row.municipalities, row.clusterId),
        {
          id: 'county',
          minSize: 250,
          cell: (info) => info.getValue(),
        },
      ),
    ];
  }, [clusters, getMunicipalityNumberByName, tOneWayIn]);
};
